import React, { useEffect, useMemo, useState } from "react";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import DetailSaveStatus from "../components/DetailSaveStatus";
import InlinePickInput from "../components/InlinePickInput";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import { TableActionColumnHeader } from "../components/TableShell";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { validateOptionalEmail, validateOptionalPhone } from "../shared/utils/validation";
import { parseOptionalFiniteNumber } from "../shared/utils/csvNumber";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { IconEdit, IconDuplicate, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import PhoneInput from "../components/PhoneInput";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import LinkifiedEditableRead from "../components/LinkifiedEditableRead";
import StarRating from "../components/StarRating";
import VendorEntityCard from "../components/VendorEntityCard";
import AssetManagementSectionTabs from "../components/AssetManagementSectionTabs";
import { ASSET_TAB_VENDORS } from "../shared/utils/assetManagementTabs";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

const SORT_KEY_TYPES = { rating: "number" };
/** Same threshold as the former Top Rated Vendors page. */
const TOP_RATED_MIN_RATING = 3;

function getVendorStarRating(obj) {
  const r = obj?.rating;
  return r >= 1 && r <= 5 ? r : 0;
}

function matchesVendorSearch(obj, searchLower) {
  if (!searchLower) return true;
  return (
    (obj.name && obj.name.toLowerCase().includes(searchLower)) ||
    (obj.phone && obj.phone.toLowerCase().includes(searchLower)) ||
    (obj.email && obj.email.toLowerCase().includes(searchLower)) ||
    (obj.type && obj.type.toLowerCase().includes(searchLower)) ||
    (obj.notes && obj.notes.toLowerCase().includes(searchLower))
  );
}

const VENDOR_TYPES = [
  "",
  "Carpet",
  "General",
  "Supplier",
  "Handyman",
  "Flooring",
  "Plumbing",
  "Electrical",
  "Other",
];

function Vendor() {
  const [currentObjs, setCurrentObjs] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const [ratingFilter, setRatingFilter] = useState(""); // "" = All, "5" = 5 stars only, "4" = 4+ stars, etc.
  const [topRatedQuickFilter, setTopRatedQuickFilter] = useState(""); // "" | "topRated"
  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialViewMode: "card" });

  // Vendor detail modal state
  const [detailVendorId, setDetailVendorId] = useState(null); // null = closed or "new"
  const [detailMode, setDetailMode] = useState(""); // '', 'view', 'edit', 'new'
  const [detailName, setDetailName] = useState("");
  const [detailPhone, setDetailPhone] = useState("");
  const [detailEmail, setDetailEmail] = useState("");
  const [detailType, setDetailType] = useState("");
  const [detailNotes, setDetailNotes] = useState("");
  const [detailRating, setDetailRating] = useState(0);
  const [detailActiveField, setDetailActiveField] = useState("");
  const [detailInitialValues, setDetailInitialValues] = useState(null);

  function getVendorTypes() {
    const set = new Set();
    (VENDOR_TYPES || []).forEach((t) => {
      const v = (t != null ? String(t) : "").trim();
      if (v) set.add(v);
    });
    (currentObjs || []).forEach((v) => {
      const t = (v?.type != null ? String(v.type) : "").trim();
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  useEffect(() => {
    getDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  function getDocument() {
    if (!account_id) return;
    apiFetch(serverUrl + "/listVendors", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function duplicateDocument(currentObj) {
    const { name: n } = currentObj;
    const duplicateName = buildDuplicateLabel(n, { fallback: "Vendor" });
    apiFetch(serverUrl + "/createVendor", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        name: duplicateName,
        phone: (currentObj.phone != null ? String(currentObj.phone) : "").trim(),
        email: (currentObj.email != null ? String(currentObj.email) : "").trim(),
        type: (currentObj.type != null ? String(currentObj.type) : "").trim(),
        notes: (currentObj.notes != null ? String(currentObj.notes) : "").trim(),
        rating: currentObj.rating >= 1 && currentObj.rating <= 5 ? currentObj.rating : undefined,
      }),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) throw new Error(data || "Failed to duplicate vendor");
        const displayLabel = duplicateName.trim() || "Duplicated vendor";
        const typeLine = (currentObj.type != null ? String(currentObj.type) : "").trim() || "—";
        const ratingLine = currentObj.rating >= 1 && currentObj.rating <= 5 ? `${currentObj.rating}/5` : "—";
        const footer = `Type: ${typeLine}\nRating: ${ratingLine}`;
        let created = null;
        try {
          created = JSON.parse(data);
        } catch {
          created = null;
        }
        const openCreated = () => {
          closeConfirmDialog();
          if (created && created._id) {
            openVendorDetail(
              {
                _id: created._id,
                name: created.name != null ? String(created.name) : duplicateName,
                phone: created.phone != null ? String(created.phone) : "",
                email: created.email != null ? String(created.email) : "",
                type: created.type != null ? String(created.type) : "",
                notes: created.notes != null ? String(created.notes) : "",
                rating: created.rating >= 1 && created.rating <= 5 ? created.rating : undefined,
              },
              "edit"
            );
          }
        };
        setConfirmDialog({
          open: true,
          title: "Vendor duplicated",
          message:
            created && created._id ? (
              <DuplicateSuccessMessage linkLabel={displayLabel} onOpenDetail={openCreated} footer={footer} />
            ) : (
              `Created "${displayLabel}".\n\n${footer}`
            ),
          onConfirm: null,
        });
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    apiFetch(serverUrl + "/deleteVendor", {
      method: "POST",
      body: JSON.stringify({ account_id, _id: id }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Vendor", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  /** Update only the rating for a vendor (direct star click in table). */
  function updateVendorRating(currentObj, newRating) {
    const nameVal = (currentObj.name != null ? String(currentObj.name) : "").trim();
    if (!nameVal) return;
    apiFetch(serverUrl + "/updateVendor", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        _id: currentObj._id,
        name: nameVal,
        phone: (currentObj.phone != null ? String(currentObj.phone) : "").trim(),
        email: (currentObj.email != null ? String(currentObj.email) : "").trim(),
        type: (currentObj.type != null ? String(currentObj.type) : "").trim(),
        notes: (currentObj.notes != null ? String(currentObj.notes) : "").trim(),
        rating: newRating >= 1 && newRating <= 5 ? newRating : undefined,
      }),
    })
      .then(() => getDocument())
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  // Detail modal helpers

  function getDetailFormValues(vendor) {
    return {
      name: vendor?.name || "",
      phone: vendor?.phone || "",
      email: vendor?.email || "",
      type: vendor?.type || "",
      notes: vendor?.notes != null ? String(vendor.notes) : "",
      rating: vendor?.rating >= 1 && vendor?.rating <= 5 ? vendor.rating : 0,
    };
  }

  function applyDetailFormValues(values) {
    setDetailName(values?.name || "");
    setDetailPhone(values?.phone || "");
    setDetailEmail(values?.email || "");
    setDetailType(values?.type || "");
    setDetailNotes(values?.notes != null ? String(values.notes) : "");
    setDetailRating(values?.rating >= 1 && values?.rating <= 5 ? values.rating : 0);
  }

  function openVendorDetail(vendor, mode = "view") {
    setDetailMode(mode);
    if (!vendor && mode === "new") {
      setDetailVendorId(null);
      const initialValues = getDetailFormValues(null);
      setDetailInitialValues(initialValues);
      applyDetailFormValues(initialValues);
      setDetailActiveField("name");
      return;
    }
    if (!vendor) {
      setDetailMode("");
      return;
    }
    setDetailVendorId(vendor._id);
    const initialValues = getDetailFormValues(vendor);
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    setDetailActiveField(mode === "edit" ? "name" : "");
  }

  async function persistDetailChanges() {
    const nameVal = (detailName || "").trim();
    if (!nameVal) return;
    const emailCheck = validateOptionalEmail(detailEmail);
    if (!emailCheck.valid) { setBannerError(emailCheck.message); return; }
    const phoneCheck = validateOptionalPhone(detailPhone);
    if (!phoneCheck.valid) { setBannerError(phoneCheck.message); return; }

    const payload = {
      account_id,
      name: nameVal,
      phone: (detailPhone || "").trim(),
      email: (detailEmail || "").trim(),
      type: (detailType || "").trim(),
      notes: (detailNotes || "").trim(),
      rating: detailRating >= 1 && detailRating <= 5 ? detailRating : undefined,
    };

    const isNew = detailMode === "new" || !detailVendorId;
    const url = isNew ? serverUrl + "/createVendor" : serverUrl + "/updateVendor";
    const body = isNew ? payload : { ...payload, _id: detailVendorId };

    try {
      if (isNew) {
        const resp = await apiFetch(url, {
          method: "POST",
          body: JSON.stringify(body),
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          setBannerError((data && data.message) || "Request failed.");
          return;
        }
        if (data && data._id) {
          setDetailVendorId(data._id);
          setDetailMode("edit");
        }
      } else {
        const resp = await apiFetch(url, {
          method: "POST",
          body: JSON.stringify(body),
        });
        const text = await resp.text();
        if (!resp.ok) {
          setBannerError(text || "Request failed.");
          return;
        }
      }
      await getDocument();
      setDetailInitialValues({
        name: nameVal,
        phone: (detailPhone || "").trim(),
        email: (detailEmail || "").trim(),
        type: (detailType || "").trim(),
        notes: (detailNotes || "").trim(),
        rating: detailRating >= 1 && detailRating <= 5 ? detailRating : 0,
      });
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  function parseRecordsFromCsvText(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const nameIdx = findCsvColumnIndex(headers, ["name", "vendor", "vendor_name"]);
    const phoneIdx = findCsvColumnIndex(headers, ["phone", "phone_number"]);
    const emailIdx = findCsvColumnIndex(headers, ["email", "email_id"]);
    const typeIdx = findCsvColumnIndex(headers, ["type", "vendor_type"]);
    const notesIdx = findCsvColumnIndex(headers, ["notes"]);
    const ratingIdx = findCsvColumnIndex(headers, ["rating"]);
    if (nameIdx === -1) throw new Error("CSV must contain a name (or vendor) column");
    return rows.map((values) => {
      const ratingRaw = ratingIdx >= 0 && values[ratingIdx] !== undefined ? String(values[ratingIdx]).trim() : "";
      return {
        name: values[nameIdx] ?? "",
        phone: phoneIdx >= 0 && values[phoneIdx] !== undefined ? values[phoneIdx] : "",
        email: emailIdx >= 0 && values[emailIdx] !== undefined ? values[emailIdx] : "",
        type: typeIdx >= 0 && values[typeIdx] !== undefined ? values[typeIdx] : "",
        notes: notesIdx >= 0 && values[notesIdx] !== undefined ? values[notesIdx] : "",
        rating: ratingRaw,
      };
    });
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseRecordsFromCsvText(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (!(record.name && String(record.name).trim())) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing name`);
          continue;
        }
        const ratingRes = parseOptionalFiniteNumber(record.rating, {
          min: 0,
          max: 5,
          integerOnly: true,
          fieldName: "Rating",
        });
        if (!ratingRes.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${ratingRes.error}`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + "/createVendor", {
            method: "POST",
            body: JSON.stringify({
              account_id,
              name: String(record.name).trim(),
              phone: (record.phone != null ? String(record.phone) : "").trim(),
              email: (record.email != null ? String(record.email) : "").trim(),
              type: (record.type != null ? String(record.type) : "").trim(),
              notes: (record.notes != null ? String(record.notes) : "").trim(),
              rating: ratingRes.value === undefined ? undefined : ratingRes.value,
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }
      setConfirmDialog({
        open: true,
        title: "Import completed",
        message: formatBatchSummaryMessage("Import completed!", successCount, errorCount, errors),
        onConfirm: null,
        mode: "alert",
      });
      getDocument();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, "Error importing CSV."));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("vendor-sample.csv", [
      "name,phone,email,type,notes,rating",
      "Home Depot,,,Supplier,Reliable for supplies,5",
      "ABC Flooring,555-1234,abc@example.com,Flooring,Good quality work,4",
      "Joe Handyman,555-5678,,Handyman,,",
    ]);
  }

  function getFilteredAndSortedData() {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    const minRating = ratingFilter === "" ? null : parseInt(ratingFilter, 10); // 5 = 5 only, 4 = 4+, etc.
    let filtered = (currentObjs || []).filter((obj) => {
      if (!matchesVendorSearch(obj, searchLower)) return false;
      const r = getVendorStarRating(obj);
      if (topRatedQuickFilter === "topRated" && r < TOP_RATED_MIN_RATING) return false;
      if (minRating == null) return true;
      if (minRating === 5) return r === 5; // 5 stars only
      return r >= minRating; // 4+ = 4,5 etc.
    });
    return sortRowsByConfig(filtered, sortConfig, SORT_KEY_TYPES);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  const topRatedQuickFilterCount = useMemo(() => {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    let count = 0;
    for (const obj of currentObjs || []) {
      if (!matchesVendorSearch(obj, searchLower)) continue;
      if (getVendorStarRating(obj) >= TOP_RATED_MIN_RATING) count += 1;
    }
    return count;
  }, [currentObjs, searchTerm]);

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete vendors", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete vendors",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteVendor", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }
  const detailCurrentValues = {
    name: (detailName || "").trim(),
    phone: (detailPhone || "").trim(),
    email: (detailEmail || "").trim(),
    type: (detailType || "").trim(),
    notes: (detailNotes || "").trim(),
    rating: detailRating >= 1 && detailRating <= 5 ? detailRating : 0,
  };
  const detailHasChanges = !!detailInitialValues && Object.keys(detailCurrentValues).some((k) => detailCurrentValues[k] !== detailInitialValues[k]);
  const {
    isSaving: detailSaving,
    handleFieldBlur: handleDetailFieldBlur,
    handleFieldCommitKeyDown: handleDetailFieldCommitKeyDown,
    saveIfNeeded: saveDetailIfNeeded,
  } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(""),
  });
  const detailAutoSaveEnabled = detailMode !== "new";
  const detailFieldBlurHandler = detailAutoSaveEnabled ? handleDetailFieldBlur : undefined;
  const detailFieldCommitKeyDownHandler = detailAutoSaveEnabled ? handleDetailFieldCommitKeyDown : undefined;
  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <EntityPageScaffold
      title="Vendors"
      titleTabs={<AssetManagementSectionTabs activeTab={ASSET_TAB_VENDORS} />}
      subtitle="Manage vendors and ratings."
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          {(detailMode === "view" || detailMode === "edit" || detailMode === "new") && (
            <div className="detail-modal-overlay">
              <div
                role="button"
                tabIndex={0}
                className="detail-modal-backdrop"
                onClick={() => setDetailMode("")}
                onKeyDown={(e) => e.key === "Escape" && setDetailMode("")}
                aria-label="Close"
              />
              <div className="panel detail-modal-panel">
                <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}>
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New vendor" : "Vendor details"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {detailMode === "new" && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!detailCurrentValues.name || detailSaving}
                        onClick={async () => {
                          await saveDetailIfNeeded();
                        }}
                      >
                        Create
                      </button>
                    )}
                    <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                    <button
                      type="button"
                      className="btn-link btn-link-muted"
                      onClick={() => setDetailMode("")}
                      style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Name</label>
                      {detailMode === "new" || detailActiveField === "name" ? (
                        <input
                          autoFocus={detailMode === "new" || detailActiveField === "name"}
                          type="text"
                          value={detailName}
                          onChange={(e) => setDetailName(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("name")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailName || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Phone</label>
                      {detailMode === "new" || detailActiveField === "phone" ? (
                        <PhoneInput
                          autoFocus={detailActiveField === "phone"}
                          value={detailPhone}
                          onChange={setDetailPhone}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("phone")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {formatPhoneDisplay(detailPhone)}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Email</label>
                      {detailMode === "new" || detailActiveField === "email" ? (
                        <input
                          autoFocus={detailActiveField === "email"}
                          type="email"
                          value={detailEmail}
                          onChange={(e) => setDetailEmail(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("email")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailEmail || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Type</label>
                      {detailMode === "new" || detailActiveField === "type" ? (
                        <InlinePickInput
                          autoFocus={detailActiveField === "type"}
                          value={detailType}
                          onChange={setDetailType}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          onOptionSelect={detailFieldBlurHandler}
                          options={getVendorTypes()}
                          placeholder="Type or pick existing"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("type")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailType || "—"}
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Rating</label>
                      <StarRating
                        value={detailRating}
                        onChange={(val) => {
                          setDetailRating(val);
                          // Always clickable (same as table/card); save immediately for existing vendors.
                          if (detailMode === "new" || !detailVendorId) return;
                          setDetailInitialValues((prev) =>
                            prev ? { ...prev, rating: val >= 1 && val <= 5 ? val : 0 } : prev
                          );
                          updateVendorRating(
                            {
                              _id: detailVendorId,
                              name: detailName,
                              phone: detailPhone,
                              email: detailEmail,
                              type: detailType,
                              notes: detailNotes,
                            },
                            val
                          );
                        }}
                        readOnly={false}
                        size="small"
                      />
                    </div>
                    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Notes</label>
                      {detailMode === "new" || detailActiveField === "notes" ? (
                        <textarea
                          autoFocus={detailActiveField === "notes"}
                          value={detailNotes}
                          onChange={(e) => setDetailNotes(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          rows={3}
                          className="input input-full"
                        />
                      ) : (
                        <LinkifiedEditableRead
                          text={detailNotes}
                          onBeginEdit={() => setDetailActiveField("notes")}
                          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", minHeight: 48, whiteSpace: "pre-wrap" }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ImportCsvPanel
            expanded={importPanelExpanded}
            fileInputId="vendor-csv-import"
            importing={importing}
            columnHint="Columns: name (required), phone, email, type. Optional: notes, rating (1-5)"
            onDownloadSample={downloadSampleCSV}
            onFileChange={handleFileChange}
            onImportText={importFromCsvText}
          />

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search name, phone, email, type, notes…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <>
                <span className="toolbar-label toolbar-rating-filter">
                  <span className="toolbar-rating-star">★</span>
                  <label htmlFor="vendor-rating-filter">Filter by Rating:</label>
                </span>
                <select id="vendor-rating-filter" value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)} className="input input-min-130">
                  <option value="">All</option>
                  <option value="5">5 stars only</option>
                  <option value="4">4+ stars</option>
                  <option value="3">3+ stars</option>
                  <option value="2">2+ stars</option>
                  <option value="1">1+ stars</option>
                </select>
                <button
                  type="button"
                  className={`btn btn-sm ${topRatedQuickFilter === "topRated" ? "btn-primary-outline active" : "btn-secondary"}`}
                  onClick={() =>
                    setTopRatedQuickFilter((prev) => (prev === "topRated" ? "" : "topRated"))
                  }
                  aria-pressed={topRatedQuickFilter === "topRated"}
                  title={`Show vendors with ${TOP_RATED_MIN_RATING}+ star ratings`}
                >
                  Top rated [{topRatedQuickFilterCount}]
                </button>
                <span className="toolbar-divider" />
              </>
            }
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            onNew={() => {
              openVendorDetail(null, "new");
              setImportPanelExpanded(false);
            }}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </>
      }
      list={
        <div className="table-scroll">
        {viewMode === "table" && (
        <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th className="text-center">
                <input
                  type="checkbox"
                  checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                  onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                  disabled={filteredRows.length === 0}
                />
              </th>
              <th className="sortable" onClick={() => handleSort("name")}>Name</th>
              <th className="sortable" onClick={() => handleSort("phone")}>Phone</th>
              <th className="sortable" onClick={() => handleSort("email")}>Email</th>
              <th className="sortable" onClick={() => handleSort("type")}>Type</th>
              <th className="sortable" onClick={() => handleSort("rating")}>Rating</th>
              <th>Notes</th>
              <TableActionColumnHeader />
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((currentObj) => (
                <tr key={currentObj._id}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(currentObj._id)}
                      onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => openVendorDetail(currentObj, "view")}
                    >
                      {currentObj.name}
                    </button>
                  </td>
                  <td>{formatPhoneDisplay(currentObj.phone)}</td>
                  <td>{currentObj.email || "—"}</td>
                  <td>{currentObj.type || "—"}</td>
                  <td className="table-cell-rating">
                    <StarRating value={currentObj.rating >= 1 && currentObj.rating <= 5 ? currentObj.rating : 0} onChange={(val) => updateVendorRating(currentObj, val)} readOnly={false} size="small" />
                  </td>
                  <td className="table-cell-notes">{currentObj.notes ? currentObj.notes : "—"}</td>
                  <td className="text-center">
                    <RowActionIcons>
                      <button type="button" onClick={() => openVendorDetail(currentObj, "edit")} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                      <button type="button" onClick={() => duplicateDocument(currentObj)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                      <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                    </RowActionIcons>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        </div>
        )}
        {viewMode === "card" && (
          <div className="cards-wrapper">
            {filteredRows.map((v) => (
              <VendorEntityCard
                key={v._id}
                vendor={v}
                selected={selectedIds.has(v._id)}
                onSelectChange={(checked) => handleSelectOne(v._id, checked)}
                onTitleClick={() => openVendorDetail(v, "view")}
                onRatingChange={(val) => updateVendorRating(v, val)}
                onEdit={() => openVendorDetail(v, "edit")}
                onDuplicate={() => duplicateDocument(v)}
                onDelete={() => deleteDocument(v)}
              />
            ))}
          </div>
        )}
        </div>
      }
    />
  );
}

export default Vendor;
